import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  LinearProgress,
  Paper,
  Stack,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { QRCodeCanvas } from "qrcode.react";
import ReplayIcon from "@mui/icons-material/Replay";
import GetAppIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import axios from "axios";
import TopBar from "./TopBar";

const PageContainer = styled(Box)(() => ({
  height: "100vh",
  overflow: "hidden",
  background: "#ffffff",
  display: "flex",
  flexDirection: "column",
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  overflow: "auto",
}));

const QRCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 640,
  margin: "0 auto",
  padding: theme.spacing(5),
  borderRadius: theme.spacing(2.5),
  border: "1px solid #f0f0f0",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

export default function QRCodePage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0); // 0 = live, 1 = printable
  const [qrValue, setQrValue] = useState("");
  const [qrStaticValue, setQrStaticValue] = useState("");
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  // 320px overflows a 360px-wide phone once card padding is counted.
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const qrSize = isNarrow ? 232 : 320;
  const [staticLoading, setStaticLoading] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [noReward, setNoReward] = useState(false);
  const flyerRef = useRef(null);

  // Accent colour for the branded printout — vendor brand colour, else app default
  const accent = brandColor || "#ffb31b";

  const token = localStorage.getItem("token");

  // Fetch vendor name
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/vendor/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
        const v = res.data?.vendor || {};
        setVendorName(v.company || "");
        setBrandColor(v.brand_color || "");

        // Pull the logo as a same-origin data URI so it embeds cleanly in the
        // printout PNG (a cross-origin <img> fails to render into html2canvas).
        if (v.logo) {
          try {
            const imgRes = await axios.get(`/api/vendor/${userId}/logo-image`, { headers: { Authorization: `Bearer ${token}` } });
            if (imgRes.data?.dataUrl) setLogoDataUrl(imgRes.data.dataUrl);
          } catch {}
        }
      } catch {}
    })();
  }, [userId, token]);

  // Fetch rotating QR
  const fetchQr = useCallback(async () => {
    try {
      setLoading(true);
      setNoReward(false);
      const res = await axios.get(`/api/qrcode/generate/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setQrValue(res.data.token);
    } catch (err) {
      if (err.response?.status === 404) setNoReward(true);
      console.error("Failed to fetch QR:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  // Fetch printable QR
  const fetchPrintable = useCallback(async () => {
    try {
      setStaticLoading(true);
      const res = await axios.post("/api/qrcode/printable", { vendorId: userId }, { headers: { Authorization: `Bearer ${token}` } });
      setQrStaticValue(res.data.token);
    } catch (err) {
      console.error("Failed to fetch printable QR:", err);
    } finally {
      setStaticLoading(false);
    }
  }, [userId, token]);

  // Auto-rotate live QR every 60s
  useEffect(() => {
    fetchQr();
    const interval = setInterval(fetchQr, 60000);
    return () => clearInterval(interval);
  }, [fetchQr]);

  // Load printable QR when switching to that tab
  useEffect(() => {
    if (tab === 1 && !qrStaticValue) fetchPrintable();
  }, [tab, qrStaticValue, fetchPrintable]);

  // Progress bar countdown
  useEffect(() => {
    if (tab !== 0) return;
    setProgress(0);
    const iv = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 100 / 60));
    }, 1000);
    return () => clearInterval(iv);
  }, [tab, qrValue]);

  const handleRegenerate = async () => {
    await fetchQr();
    setProgress(0);
  };

  const handleExportPNG = async () => {
    if (!qrStaticValue) await fetchPrintable();
    await new Promise((r) => setTimeout(r, 400));

    if (flyerRef.current) {
      const canvas = await html2canvas(flyerRef.current, {
        backgroundColor: "#fff",
        scale: 3,
        useCORS: true,
      });
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `${vendorName || "Rewarding"}-QR-Code.png`;
      link.href = url;
      link.click();
    }
  };

  const secondsRemaining = Math.max(0, Math.ceil(60 - (progress / 100) * 60));

  if (noReward) {
    return (
      <PageContainer>
        <TopBar />
        <ContentContainer maxWidth="xl">
          <Box sx={{ textAlign: "center", mt: 10 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: 3, bgcolor: "#fff8e1", display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
              <Typography sx={{ fontSize: 32 }}>🎁</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}>
              Set up your reward first
            </Typography>
            <Typography sx={{ color: "#999", fontSize: 14, maxWidth: 380, mx: "auto", mb: 4, lineHeight: 1.7 }}>
              You need to create a reward program before customers can scan your QR code. Head to the Rewards page to set one up.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/rewards/${userId}`)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#ffb31b",
                color: "#000",
                borderRadius: 3,
                px: 4,
                py: 1.2,
                "&:hover": { bgcolor: "#e6a118" },
              }}
            >
              Go to Rewards
            </Button>
          </Box>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: "#1a1a2e", mb: 0.5 }}>
            QR Code
          </Typography>
          <Typography variant="body2" sx={{ color: "#999" }}>
            Customers scan this with the Rewarding app to earn stamps
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ maxWidth: 640, mx: "auto", mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                minHeight: 40,
                color: "#999",
                "&.Mui-selected": { color: "#1a1a2e" },
              },
              "& .MuiTabs-indicator": { backgroundColor: "#ffb31b", height: 2.5, borderRadius: 2 },
            }}
          >
            <Tab label="Live QR" />
            <Tab label="Printable QR" />
          </Tabs>
        </Box>

        {/* Live QR Tab */}
        {tab === 0 && (
          <QRCard>
            <Chip
              size="small"
              label="Rotates every 60s"
              sx={{ mb: 2, bgcolor: "#fff8e1", color: "#b27a00", fontWeight: 600, fontSize: 11 }}
            />

            <Box sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px solid #f5f5f5",
              mb: 2,
            }}>
              {loading || !qrValue ? (
                <Box sx={{ width: qrSize, height: qrSize, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                </Box>
              ) : (
                <QRCodeCanvas
                  value={qrValue}
                  size={qrSize}
                  level="H"
                  includeMargin
                  style={{ display: "block" }}
                />
              )}
            </Box>

            <Box sx={{ width: "100%", mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#f0f0f0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background: "linear-gradient(90deg, #ffb31b, #ff9e00)",
                  },
                }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "center", color: "#bbb" }}>
                New code in {secondsRemaining}s
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={handleRegenerate}
              startIcon={<ReplayIcon />}
              fullWidth
              sx={{
                mt: 1,
                textTransform: "none",
                borderRadius: 2,
                borderColor: "#e0e0e0",
                color: "#666",
                fontWeight: 600,
                fontSize: 13,
                "&:hover": { borderColor: "#ccc", bgcolor: "#fafafa" },
              }}
            >
              Regenerate Now
            </Button>
          </QRCard>
        )}

        {/* Printable QR Tab */}
        {tab === 1 && (
          <QRCard>
            <Chip
              size="small"
              label="Valid for 1 year"
              sx={{ mb: 2, bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, fontSize: 11 }}
            />

            <Box sx={{
              display: "flex",
              justifyContent: "center",
              p: 2,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px solid #f5f5f5",
              mb: 2,
            }}>
              {staticLoading || !qrStaticValue ? (
                <Box sx={{ width: qrSize, height: qrSize, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary">Generating...</Typography>
                </Box>
              ) : (
                <QRCodeCanvas
                  value={qrStaticValue}
                  size={320}
                  level="H"
                  includeMargin
                  style={{ display: "block" }}
                />
              )}
            </Box>

            <Typography variant="body2" sx={{ color: "#999", fontSize: 12, textAlign: "center", mb: 2 }}>
              This QR code stays the same — print it for your counter, receipts, or flyers.
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
              <Button
                variant="outlined"
                onClick={fetchPrintable}
                startIcon={<ReplayIcon />}
                fullWidth
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  borderColor: "#e0e0e0",
                  color: "#666",
                  fontWeight: 600,
                  fontSize: 13,
                  "&:hover": { borderColor: "#ccc", bgcolor: "#fafafa" },
                }}
              >
                New Code
              </Button>
              <Button
                variant="contained"
                onClick={handleExportPNG}
                startIcon={<GetAppIcon />}
                fullWidth
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "#1a1a2e",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  "&:hover": { bgcolor: "#2a2a4e" },
                }}
              >
                Export PNG
              </Button>
            </Stack>
          </QRCard>
        )}

        {/* Hidden flyer for PNG export */}
        <Box
          ref={flyerRef}
          sx={{
            width: 520,
            background: "#ffffff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            fontFamily: '"Inter", "Helvetica Neue", sans-serif',
            overflow: "hidden",
            borderRadius: 4,
            border: `1px solid ${accent}22`,
          }}
        >
          {/* Branded header band */}
          <Box
            sx={{
              width: "100%",
              py: 4,
              px: 4,
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {logoDataUrl ? (
              <Box
                component="img"
                src={logoDataUrl}
                alt={vendorName}
                sx={{
                  maxHeight: 96,
                  maxWidth: 260,
                  objectFit: "contain",
                  mb: 1.5,
                  borderRadius: 2,
                  background: "#fff",
                  p: 1,
                }}
              />
            ) : null}
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: "2rem",
                color: "#ffffff",
                lineHeight: 1.1,
                textShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              {vendorName || "Rewarding"}
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ py: 4, px: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
            {/* QR */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: `3px solid ${accent}`,
                mb: 3,
              }}
            >
              <QRCodeCanvas
                value={qrStaticValue || "LOADING"}
                size={300}
                level="H"
                bgColor="#ffffff"
                fgColor="#1a1a2e"
              />
            </Box>

            <Typography
              sx={{ fontWeight: 800, fontSize: "1.35rem", mb: 0.75, color: "#1a1a2e" }}
            >
              Scan to earn rewards
            </Typography>
            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "#666",
                maxWidth: 360,
                lineHeight: 1.5,
              }}
            >
              Open the Rewarding app, scan this code after your purchase, and collect stamps towards free rewards.
            </Typography>
          </Box>

          {/* Footer band */}
          <Box
            sx={{
              width: "100%",
              py: 1.5,
              background: "#1a1a2e",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.75rem", color: "#ffffff", letterSpacing: 1.5, opacity: 0.85 }}>
              POWERED BY REWARDING
            </Typography>
          </Box>
        </Box>
      </ContentContainer>
    </PageContainer>
  );
}
